import { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { PjSandboxBoardKey } from '@cortex/domain-product-innovation-lab-types';
import { useCombatPower, useDailyWorkRatios } from '../../../../hooks/useCombatPower.js';
import { usePjSandboxHistory } from '../../../../hooks/usePjSandbox.js';
import { TabButton, TabGroup } from '../../../ui/Tabs/index.js';
import { BlockEditModal } from './BlockEditModal.js';
import { CandidatePool } from './CandidatePool.js';
import { EditHistoryPanel } from './EditHistoryPanel.js';
import { SandboxBoard } from './SandboxBoard.js';
import { TicketImportModal } from './TicketImportModal.js';
import { pendingTicketItems, toTicketBlockDraft } from './ticketImport.js';
import { planTicketPlacements } from './autoPlacement.js';
import { useTicketImport } from './useTicketImport.js';
import { placementToken } from './persistence.js';
import { mergeSaveNotices, usePjSandboxPersistence } from './usePjSandboxPersistence.js';
import { useSharedTrayPersistence } from './useSharedTrayPersistence.js';
import { SHARED_TRAY_TEAM_KEY, newTrayBlockId, partitionBlocks } from './sharedTray.js';
import { useSandboxDnd } from './useSandboxDnd.js';
import { assignLanes } from './layout.js';
import {
  HEADER_HEIGHT,
  LANE_MIN_HEIGHT,
  ROW_HEIGHT,
  ROW_PAD,
  generateDays,
  generatePoolWeeks,
} from './constants.js';
import { TEAM_DEFS, buildTeamsFromMemberMap } from './teamRoster.js';
import { detectCurrentMonthIdx } from '../../combat-power/combat-power-utils.js';
import type { TicketLookupItem } from '../../../../types/project.js';
import type { BlockKind, SandboxBlock, SandboxDay, SandboxMember } from './types.js';

/** 編集履歴ブロックの初期表示件数。「もっと見る」でこの単位ずつ増やす。 @graph-connects none */
const HISTORY_PAGE_SIZE = 50;

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox（PJ 予定シミュレーション白板）のトップビュー。PDG/ACE 各チームの白板をタブ切替し、日単位ガント風グリッド（過去1週〜未来4週・休日表示）と候補案件プール、編集履歴ブロックを束ねる。配置済みブロックと候補案件はチーム白板ごとに、未配置ブロックは全チーム共通の 1 プール（共有トレイ）として保存され、チームを切り替えても未配置ブロックは入れ替わらない。チーム白板の文書に未配置のまま残っているブロック（共有トレイ導入前の保存・担当者が名簿から消えた予定）は白板を開いた時点で共有トレイへ引き取り、トレイへ収まってから白板文書から落とす。案件番号での一括取込は、開発リーダーが埋まっている案件を担当メンバーの行（既存予定の次の営業日から、見積もり pt ÷ 当月の期待戦闘力ぶんの営業日を覆う長さ）へ自動配置し、それ以外を共有トレイへ落とす。編集は PDG/ACE の PMO・チームリーダー・マネージャーのみで、それ以外は閲覧専用になる。DnD は useSandboxDnd、行高・レーン割当などのレイアウト計算をここで組み立てて各描画コンポーネントへ渡す
 * @graph-connects SandboxBoard [calls] 白板グリッド描画
 * @graph-connects CandidatePool [calls] 候補案件プール描画
 * @graph-connects BlockEditModal [calls] ブロック属性編集モーダル描画
 * @graph-connects TicketImportModal [calls] チケット一括取込モーダル描画
 * @graph-connects EditHistoryPanel [calls] 編集履歴ブロック描画
 * @graph-connects useTicketImport [calls] チケット一括取込の状態管理と照会実行
 * @graph-connects planTicketPlacements [calls] 開発リーダー付き案件の担当メンバー行への自動配置計画
 * @graph-connects placementToken [calls] 編集履歴に載せる配置トークンの組み立て
 * @graph-connects usePjSandboxPersistence [calls] チーム白板の共有保存（読み込み・解決・自動保存・編集権限）
 * @graph-connects useSharedTrayPersistence [calls] 全チーム共通の未配置トレイの共有保存
 * @graph-connects partitionBlocks [calls] ブロックの保存先（チーム白板 / 共有トレイ）への振り分け
 * @graph-connects useSandboxDnd [calls] 白板のドラッグ&ドロップ操作
 * @graph-connects usePjSandboxHistory [calls] 編集履歴の取得
 * @graph-connects TabGroup [calls] チーム切替タブ描画
 */
export function PjSandboxView() {
  const { t } = useTranslation();
  const [selectedTeam, setSelectedTeam] = useState<PjSandboxBoardKey>(TEAM_DEFS[0]?.key ?? 'pdg');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);

  const canvasRef = useRef<HTMLDivElement>(null);
  // 白板の可視スクロール領域（両軸スクロールコンテナ）。ドラッグの「白板内 / 外」判定に使う。
  const boardViewportRef = useRef<HTMLDivElement>(null);

  // 横軸の日付・候補プールの週セクションは初回のみ生成して固定する。
  const days = useMemo<SandboxDay[]>(() => generateDays(), []);
  const poolWeeks = useMemo(() => generatePoolWeeks(), []);

  /**
   * 今日の日インデックス。チケット取込の自動配置が過去の列に置かないための下限に使う。
   * `generateDays` は今日を必ず含むが、万一見つからなければ先頭列へ丸める。
   */
  const todayIndex = useMemo(() => {
    const index = days.findIndex((d) => d.isToday);
    return index === -1 ? 0 : index;
  }, [days]);

  // 実データ: 戦闘力サマリー API の memberTeamMap から各白板のメンバー行を組み立てる。
  const combatPower = useCombatPower();
  const teams = useMemo(
    () => buildTeamsFromMemberMap(combatPower.data?.memberTeamMap ?? []),
    [combatPower.data],
  );
  const membersLoading = combatPower.isLoading;

  const team = useMemo(
    () => teams.find((tm) => tm.key === selectedTeam) ?? teams[0],
    [teams, selectedTeam],
  );
  const members = useMemo<SandboxMember[]>(() => team?.members ?? [], [team]);

  /**
   * ニックネーム（小文字）→ 当月の期待戦闘力（1 営業日あたりに倒せる pt）。
   * チケット取込の自動配置で、見積もり pt を仮の所要営業日数へ換算するのに使う。
   * 期待値が未入力 / 0 のメンバーは載せず、自動配置の対象から外す（長さを決められないため）。
   */
  const expectedPowerByNick = useMemo(() => {
    const data = combatPower.data;
    const map = new Map<string, number>();
    if (data === undefined) return map;
    const monthIdx = detectCurrentMonthIdx(data.monthHeaders);
    for (const member of data.members) {
      const power = member.months[monthIdx]?.expectedPower;
      if (power !== null && power !== undefined && power > 0) {
        map.set(member.name.trim().toLowerCase(), power);
      }
    }
    return map;
  }, [combatPower.data]);

  // チーム共有の保存（読み込み・解決・自動保存・楽観ロック）は専用フックが引き受ける。
  // メンバー行が揃うまでは解決させない: 空の名簿に対して解決すると配置済みブロックが
  // 軒並み「担当者不明」でトレイへ落ち、その状態を保存し返してしまう。
  const {
    blocks: boardBlocks,
    setBlocks: setBoardBlocks,
    candidates,
    setCandidates,
    carryOverCandidateIds,
    canEdit,
    loading: boardLoading,
    saveNotice: boardSaveNotice,
    adoptedTrayBlocks,
    confirmTrayAdoption,
    recordOp,
    nextBlockId,
    nextCandidateId,
  } = usePjSandboxPersistence({
    boardKey: selectedTeam,
    members,
    days,
    weeks: poolWeeks,
    rosterReady: !membersLoading && members.length > 0,
  });

  // 未配置ブロックは「まだ誰の・いつの予定でもない案件」なので、チーム白板ごとではなく
  // **全チーム共通の 1 プール** として別ドキュメントに持つ。チームタブを切り替えても
  // ここだけは入れ替わらない。チーム白板の文書に未配置のまま残っていたぶん
  // (`adoptedTrayBlocks`) もここへ引き取る。
  const {
    blocks: trayPoolBlocks,
    setBlocks: setTrayPoolBlocks,
    saveNotice: traySaveNotice,
  } = useSharedTrayPersistence({
    adopted: adoptedTrayBlocks,
    onAdoptionPersisted: confirmTrayAdoption,
  });

  /**
   * 白板（配置済み）と共有トレイ（未配置）を、DnD や各種ハンドラからは 1 本の配列として扱う。
   * 保存先が 2 つに分かれているのはここから下には見せず、`setBlocks` の中で
   * `placement` を見て振り分ける。
   */
  const blocks = useMemo(() => [...boardBlocks, ...trayPoolBlocks], [boardBlocks, trayPoolBlocks]);
  // 同じ tick 中に setBlocks が続けて呼ばれても続きから積めるよう、更新後の配列を同期的に持つ。
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const setBlocks = useCallback<Dispatch<SetStateAction<SandboxBlock[]>>>(
    (action) => {
      const next = typeof action === 'function' ? action(blocksRef.current) : action;
      const { placed, tray } = partitionBlocks(next, selectedTeam);
      blocksRef.current = [...placed, ...tray];
      setBoardBlocks(placed);
      setTrayPoolBlocks(tray);
    },
    [selectedTeam, setBoardBlocks, setTrayPoolBlocks],
  );

  // 保存状態は白板とトレイの 2 系統あるが、画面には切迫している方だけを出す。
  const saveNotice = useMemo(
    () => mergeSaveNotices(boardSaveNotice, traySaveNotice),
    [boardSaveNotice, traySaveNotice],
  );

  // 実データ: 稼働日計算タブ由来の日次稼働割合（0=休息日 / >0=稼働日）を横軸の期間ぶん取得する。
  const rangeStart = days[0]?.iso;
  const rangeEnd = days[days.length - 1]?.iso;
  const workRatios = useDailyWorkRatios(rangeStart, rangeEnd);

  // ニックネーム(小文字) → { 日付ISO: 稼働割合 }。
  const ratioByNick = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const m of workRatios.data?.members ?? []) map.set(m.nickName.toLowerCase(), m.ratios);
    return map;
  }, [workRatios.data]);

  // メンバー行 × 日 の休息日マトリクス。シートに値がある日は 0=休息、無い日は暦(週末)へフォールバック。
  const restMatrix = useMemo(
    () =>
      members.map((mem) => {
        const ratios =
          mem.nickName !== undefined ? ratioByNick.get(mem.nickName.toLowerCase()) : undefined;
        return days.map((d) => {
          const r = ratios?.[d.iso];
          if (r !== undefined) return r === 0;
          return d.isWeekend;
        });
      }),
    [members, days, ratioByNick],
  );

  const placedBlocks = useMemo(() => blocks.filter((b) => b.placement !== null), [blocks]);
  const trayBlocks = useMemo(() => blocks.filter((b) => b.placement === null), [blocks]);

  // 白板に配置済み（＝候補から作られたブロックが存在する）候補案件の ID 集合。
  // ブロックを削除すると自動で空になり、候補側の表示も未配置に戻る。
  // 表示期間の外へ出て描画していないブロックのぶん (`carryOverCandidateIds`) も足す。
  // 描画中のブロックだけで判定すると、週明けに表示期間が動いただけで終わった予定の候補が
  // 「未配置」へ戻り、もう白板に置いた案件を未着手として拾い直してしまう。
  const arrangedCandidateIds = useMemo(() => {
    const ids = new Set<string>(carryOverCandidateIds);
    for (const b of blocks) {
      if (b.sourceCandidateId !== undefined) ids.add(b.sourceCandidateId);
    }
    return ids;
  }, [blocks, carryOverCandidateIds]);

  // 重なり並列（レーン）割当。同一行で日付が重なるブロックを縦分割して並列表示する。
  const lanes = useMemo(() => assignLanes(placedBlocks), [placedBlocks]);

  // 行ごとの高さ（重なりが多い行は自動で高くして、各レーンに名称+pt が収まるようにする）。
  const rowHeights = useMemo(
    () =>
      members.map((_, i) => {
        let laneCount = 1;
        for (const b of placedBlocks) {
          const p = b.placement;
          if (p === null || p.rowIndex !== i) continue;
          const l = lanes.get(b.id);
          if (l !== undefined && l.laneCount > laneCount) laneCount = l.laneCount;
        }
        return Math.max(ROW_HEIGHT, laneCount * LANE_MIN_HEIGHT + ROW_PAD);
      }),
    [members, placedBlocks, lanes],
  );

  // 各行の上端 Y（キャンバス基準。ヘッダー高を含む）。
  const rowTops = useMemo(() => {
    const tops: number[] = [];
    let acc = HEADER_HEIGHT;
    for (const h of rowHeights) {
      tops.push(acc);
      acc += h;
    }
    return tops;
  }, [rowHeights]);

  const trackHeight = useMemo(() => rowHeights.reduce((a, b) => a + b, 0), [rowHeights]);

  // ブロックの移動 / リサイズ / 候補の配置・週移動といった掴んで離す操作一式。
  const { preview, hoverWeek, onBlockPointerDown, onResizePointerDown, onCandidatePointerDown } =
    useSandboxDnd({
      canvasRef,
      boardViewportRef,
      blocks,
      setBlocks,
      setCandidates,
      members,
      days,
      weeks: poolWeeks,
      rowTops,
      rowHeights,
      teamKey: selectedTeam,
      canEdit,
      recordOp,
      nextBlockId,
      nextCandidateId,
    });

  const editingBlock = useMemo(
    () => blocks.find((b) => b.id === editingId) ?? null,
    [blocks, editingId],
  );

  /**
   * チーム白板を切り替える。切替前の保存待ちの書き出しと状態リセットは
   * usePjSandboxPersistence が boardKey の変化を見て行うため、ここでは
   * 表示側の状態（履歴の表示件数）だけを戻す。
   */
  const handleSelectTeam = useCallback(
    (key: PjSandboxBoardKey) => {
      if (key === selectedTeam) return;
      setSelectedTeam(key);
      setHistoryLimit(HISTORY_PAGE_SIZE);
    },
    [selectedTeam],
  );

  const handleDeleteBlock = useCallback(
    (blockId: string) => {
      if (!canEdit) return;
      const target = blocks.find((b) => b.id === blockId);
      setBlocks((prev) => prev.filter((b) => b.id !== blockId));
      recordOp({
        action: 'block_delete',
        targetName: target?.name ?? '',
        before: null,
        after: null,
        count: null,
      });
    },
    [canEdit, blocks, setBlocks, recordOp],
  );

  const handleEditBlock = useCallback(
    (block: SandboxBlock) => {
      if (!canEdit) return;
      setEditingId(block.id);
    },
    [canEdit],
  );

  const handleSaveEdit = useCallback(
    (
      id: string,
      patch: { name: string; sizePt: number; kind: BlockKind; issueKey: string | undefined },
    ) => {
      if (!canEdit) return;
      const before = blocks.find((b) => b.id === id);
      // issueKey は undefined で「課題キーを外す」を意味するため、スプレッドではなく明示代入する。
      setBlocks((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
                ...b,
                name: patch.name,
                sizePt: patch.sizePt,
                kind: patch.kind,
                issueKey: patch.issueKey,
              }
            : b,
        ),
      );
      recordOp({
        action: 'block_edit',
        targetName: patch.name,
        before: before === undefined ? null : `${before.name} ${before.sizePt.toString()}pt`,
        after: `${patch.name} ${patch.sizePt.toString()}pt`,
        count: null,
      });
    },
    [canEdit, blocks, setBlocks, recordOp],
  );

  const handleCloseEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  // プールの「候補追加」は未配置ブロックを直接生成する。未配置ブロックは全チーム共通の
  // プールなので、表示中のチームではなく共有トレイの teamKey / ID 体系で作る。
  const handleAddToTray = useCallback(
    (input: { title: string; sizePt: number; kind: BlockKind }) => {
      if (!canEdit) return;
      setBlocks((prev) => [
        ...prev,
        {
          id: newTrayBlockId(),
          teamKey: SHARED_TRAY_TEAM_KEY,
          placement: null,
          name: input.title,
          sizePt: input.sizePt,
          kind: input.kind,
        },
      ]);
      recordOp({
        action: 'block_create',
        targetName: input.title,
        before: null,
        after: null,
        count: null,
      });
    },
    [canEdit, setBlocks, recordOp],
  );

  /**
   * チケット一括取込。案件番号を照会し、ブロックをまとめて生成する。
   *
   * 開発リーダーが決まっている案件は、その担当メンバーの行へそのまま配置する（既存予定の
   * 次の営業日から、見積もり pt ÷ 当月の期待戦闘力ぶんの営業日を覆う長さ）。担当が決まって
   * いない / この白板の名簿に居ない / 長さを決められない案件は、従来どおり全チーム共通の
   * 共有トレイへ未配置で落とす。配置済みと未配置で ID 空間と teamKey が別なので、
   * 計画の結果に合わせて作り分ける。
   */
  const handleImportedTickets = useCallback(
    (items: TicketLookupItem[]) => {
      if (!canEdit) return;
      // 既に同じ課題キーを持つブロックがある案件は取り込まない（貼り直しでの増殖を防ぐ）。
      const pending = pendingTicketItems(blocks, items);
      if (pending.length === 0) return;

      const plans = planTicketPlacements({
        items: pending,
        blocks,
        members,
        restMatrix,
        expectedPowerByNick,
        earliestDay: todayIndex,
      });
      const created = plans.map<SandboxBlock>(({ item, placement }) =>
        placement === null
          ? {
              id: newTrayBlockId(),
              teamKey: SHARED_TRAY_TEAM_KEY,
              placement: null,
              ...toTicketBlockDraft(item),
            }
          : { id: nextBlockId(), teamKey: selectedTeam, placement, ...toTicketBlockDraft(item) },
      );

      setBlocks([...blocks, ...created]);
      recordOp({
        action: 'ticket_import',
        targetName: '',
        before: null,
        after: null,
        count: created.length,
      });
      // 自動配置は誰も掴んでいないので、取込件数だけでは「なぜ自分の行に載ったのか」が
      // 追えない。手で配置したときと同じ block_place を 1 件ずつ残して担当と開始日を示す。
      for (const block of created) {
        if (block.placement === null) continue;
        recordOp({
          action: 'block_place',
          targetName: block.name,
          before: null,
          after: placementToken(block.placement, members, days),
          count: null,
        });
      }
    },
    [
      canEdit,
      blocks,
      members,
      days,
      restMatrix,
      expectedPowerByNick,
      todayIndex,
      selectedTeam,
      nextBlockId,
      setBlocks,
      recordOp,
    ],
  );
  const ticketImport = useTicketImport(handleImportedTickets);

  const handleDeleteCandidate = useCallback(
    (id: string) => {
      if (!canEdit) return;
      const target = candidates.find((c) => c.id === id);
      setCandidates((prev) => prev.filter((c) => c.id !== id));
      // 候補案件を削除したら、その候補から生成済みの白板ブロックに残る
      // sourceCandidateId の無効な参照を除去する（ブロック自体は残す）
      setBlocks((prev) =>
        prev.map((b) => (b.sourceCandidateId === id ? { ...b, sourceCandidateId: undefined } : b)),
      );
      recordOp({
        action: 'candidate_delete',
        targetName: target?.title ?? '',
        before: null,
        after: null,
        count: null,
      });
    },
    [canEdit, candidates, setBlocks, setCandidates, recordOp],
  );

  // 編集履歴。白板と同じチーム単位で、新しい順に取得する。
  const historyQuery = usePjSandboxHistory(selectedTeam, { limit: historyLimit, enabled: true });

  const handleShowMoreHistory = useCallback(() => {
    setHistoryLimit((prev) => prev + HISTORY_PAGE_SIZE);
  }, []);

  const saveStatusText = useMemo(() => {
    switch (saveNotice.kind) {
      case 'saving':
        return t('pjSandbox.save.saving');
      case 'saved':
        return t('pjSandbox.save.saved', { time: saveNotice.at });
      case 'failed':
        return t('pjSandbox.save.failed');
      case 'forbidden':
        return t('pjSandbox.save.forbidden');
      case 'conflict':
        return t('pjSandbox.save.conflict', { actor: saveNotice.actor });
      default:
        return null;
    }
  }, [saveNotice, t]);

  const saveStatusIsError =
    saveNotice.kind === 'failed' ||
    saveNotice.kind === 'forbidden' ||
    saveNotice.kind === 'conflict';

  return (
    // ページは通常どおり document スクロールに任せる（ビューポート高への固定はしない）。
    //
    // 以前はビュー全体を `h-[calc(100dvh-…)]` でビューポート高に収め、上部（見出し/タブ/
    // 候補プール/未配置ブロック）を固定して白板だけ内部スクロールさせていた。だが白板の下に
    // 編集履歴ブロックが増えたことで、残り高さの取り合いで **白板が数行ぶんまで潰れる** ように
    // なった。高さの奪い合いを止め、白板側に「最低 MIN_VISIBLE_ROWS 行は見える」高さを
    // 持たせる方式へ変えている（`BOARD_MIN_HEIGHT` / `BOARD_MAX_HEIGHT`）。
    //
    // 上部が一緒にスクロールアウトする代わりに、日付ヘッダーとメンバー列は白板の内部 sticky で
    // 残るため、行と日付の対応は読めたままになる。
    <div className="flex flex-col gap-4">
      <header className="flex shrink-0 flex-col gap-2">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('pjSandbox.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">{t('pjSandbox.description')}</p>
      </header>

      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2">
        <TabGroup ariaLabel={t('pjSandbox.teamSwitch')}>
          {teams.map((tm) => (
            <TabButton
              key={tm.key}
              active={selectedTeam === tm.key}
              label={tm.label}
              onClick={() => {
                handleSelectTeam(tm.key);
              }}
            />
          ))}
        </TabGroup>

        {/* 保存状態 / 権限の告知。共有白板なので「いま保存されたか」が常に見えるようにする。 */}
        {saveStatusText !== null && (
          <span
            role="status"
            className="text-xs"
            style={{
              color: saveStatusIsError ? 'var(--color-error)' : 'var(--color-text-muted)',
            }}
          >
            {saveStatusText}
          </span>
        )}
        {!canEdit && !boardLoading && (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('pjSandbox.readOnlyNotice')}
          </span>
        )}
      </div>

      {/* 候補案件プール（上部固定）。未配置ブロックのトレイまでは固定し、白板グリッドだけスクロールする。 */}
      <div className="min-w-0 shrink-0">
        <CandidatePool
          candidates={candidates}
          weeks={poolWeeks}
          hoverWeekIndex={hoverWeek}
          arrangedIds={arrangedCandidateIds}
          canEdit={canEdit}
          onAddCandidate={handleAddToTray}
          onDeleteCandidate={handleDeleteCandidate}
          onCandidatePointerDown={onCandidatePointerDown}
          onOpenTicketImport={ticketImport.openModal}
        />
      </div>

      <div className="flex min-w-0 flex-col">
        <SandboxBoard
          canvasRef={canvasRef}
          boardViewportRef={boardViewportRef}
          members={members}
          days={days}
          placedBlocks={placedBlocks}
          trayBlocks={trayBlocks}
          preview={preview}
          lanes={lanes}
          rowHeights={rowHeights}
          rowTops={rowTops}
          trackHeight={trackHeight}
          restMatrix={restMatrix}
          membersLoading={membersLoading}
          canEdit={canEdit}
          onBlockPointerDown={onBlockPointerDown}
          onResizePointerDown={onResizePointerDown}
          onDeleteBlock={handleDeleteBlock}
          onEditBlock={handleEditBlock}
        />
      </div>

      {/* 編集履歴（白板グリッドの下）。誰がどの操作をしたかを新しい順に並べる。 */}
      <EditHistoryPanel
        entries={historyQuery.data?.entries ?? []}
        loading={historyQuery.isFetching}
        failed={historyQuery.isError}
        hasMore={historyQuery.data?.hasMore ?? false}
        onShowMore={handleShowMoreHistory}
      />

      <BlockEditModal block={editingBlock} onClose={handleCloseEdit} onSave={handleSaveEdit} />

      <TicketImportModal
        open={ticketImport.open}
        onClose={ticketImport.closeModal}
        importing={ticketImport.importing}
        notFound={ticketImport.notFound}
        errorMessage={ticketImport.failed ? t('pjSandbox.import.error') : null}
        onImport={ticketImport.importTickets}
      />
    </div>
  );
}
